import { ScreenSize } from '../screenDetection/screenDetection';

export enum TrackProperty {
  title = 'title',
  length = 'length',
  artist = 'artist',
  album = 'album',
  dateAdded = 'dateAdded',
  releaseDate = 'releaseDate',
  type = 'type',
  genre = 'genre',
  trackNumber = 'trackNumber',
  /**
   * The account a track was posted from - a YouTube channel, a SoundCloud
   * or Mixcloud uploader. Distinct from artist: a set by one DJ is often
   * posted by a label or a station, and for YouTube there is no artist at
   * all. Hidden by default everywhere it exists.
   */
  uploader = 'uploader',
  /**
   * The rest of what a tag read fills in - see the optional fields on
   * Track. Every one starts hidden in the library and does not exist in the
   * streaming scopes, where nothing supplies it.
   */
  disc = 'disc',
  albumArtist = 'albumArtist',
  composer = 'composer',
  comment = 'comment',
  bpm = 'bpm',
  initialKey = 'initialKey',
  publisher = 'publisher',
  /**
   * What the file is rather than what its tags say: the extension it has
   * in its drive, and the bit rate its own headers give. Library only,
   * hidden until ticked, like the tag fields.
   */
  format = 'format',
  bitrate = 'bitrate',
}
export type ColumnKey = TrackProperty | 'size' | 'image';

export enum ColumnScope {
  /**
   * Layouts with the library playlists on either side and content using the remaining space
   */
  LibraryPlaylists = 'LibraryPlaylists',
  /**
   * Layouts where the main content is sandwiched between two lists
   * Usually the library playlists on left and cloud provider playlists on right side
   */
  YouTube = 'YouTube',
  YouTubeSearch = 'YouTubeSearch',
  Spotify = 'Spotify',
  SpotifyAlbumSearch = 'SpotifyAlbumSearch',
  SpotifyPlaylistSearch = 'SpotifyPlaylistSearch',
  SpotifyTrackSearch = 'SpotifyTrackSearch',
  SoundCloud = 'SoundCloud',
  SoundCloudTrackSearch = 'SoundCloudTrackSearch',
  SoundCloudPlaylistSearch = 'SoundCloudPlaylistSearch',
  /**
   * A list of Mixcloud shows - the search results, or one account's
   * uploads. Shows are tracks here, so the widths are SoundCloud's.
   */
  Mixcloud = 'Mixcloud',
  MixcloudShowSearch = 'MixcloudShowSearch',
  /** A list of accounts: a name and an avatar, nothing else. */
  MixcloudUserSearch = 'MixcloudUserSearch',

  CloudProviders = 'CloudProviders',
}

interface ColumnDefaults {
  name: string;
  trackProperty: TrackProperty;
  /**
   * Width in percentages of main viewport width.
   *
   * Three cases: a positive number is the default width, **0 means the
   * column does not exist in that scope at all** - it is filtered out
   * before any settings are written, so it never reaches the Columns menu
   * and cannot be unhidden - and **-1 means it exists but starts hidden**,
   * which is width 0 in the stored settings and a name the user can tick
   * on. Anything rendering a scope must handle every column that is not 0
   * there, because the user can unhide it.
   */
  defaultWidthPerc: {
    [S in ColumnScope]: number;
  };
  sortOrder: number;
  /** Align content right */
  alignRight?: boolean;
  /**
   * Calculate this columns default width by rendering an element with x * '0' as content
   * X = the width number.
   * The remaining columns then calculate width using viewPort - these columns and percentage
   * off the remainder.
   */
  calcFromCharCount?: boolean;
  /**
   * The narrowest a flexible column is ever shown, as a count of
   * characterForWidthCalc. Columns sized from a character count use that
   * count as their floor instead. See fitColumns.
   */
  minChars?: number;
}

/**
 * Default properties for all standard playlist columns.
 * defaultWidth is for Large screens,
 * defaults for smaller ones is to grow all columns proportionately by the missing width.
 */
const PlaylistColumns: {
  [P in TrackProperty]: ColumnDefaults;
} & {
  size: Omit<ColumnDefaults, 'trackProperty'>;
  image: Omit<ColumnDefaults, 'trackProperty'>;
} = {
  [TrackProperty.length]: {
    name: 'Time',
    trackProperty: TrackProperty.length,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 7,
      [ColumnScope.YouTube]: 7,
      [ColumnScope.Spotify]: 7,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 7,
      [ColumnScope.SoundCloud]: 7,
      [ColumnScope.SoundCloudTrackSearch]: 7,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 7,
      [ColumnScope.MixcloudShowSearch]: 7,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 0,
    alignRight: true,
    calcFromCharCount: true,
  },
  /**
   * Position on the album, from TRCK. Only the library knows it - the
   * streaming scopes get 0 here, which drops the column from those views.
   */
  [TrackProperty.trackNumber]: {
    name: '#',
    trackProperty: TrackProperty.trackNumber,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 4,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 2,
    alignRight: true,
    calcFromCharCount: true,
  },
  [TrackProperty.title]: {
    name: 'Name',
    trackProperty: TrackProperty.title,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 40,
      [ColumnScope.YouTube]: 100,
      [ColumnScope.Spotify]: 40,
      [ColumnScope.CloudProviders]: 80,
      [ColumnScope.YouTubeSearch]: 55,
      [ColumnScope.SpotifyAlbumSearch]: 60,
      [ColumnScope.SpotifyPlaylistSearch]: 90,
      [ColumnScope.SpotifyTrackSearch]: 40,
      [ColumnScope.SoundCloud]: 62,
      [ColumnScope.SoundCloudTrackSearch]: 62,
      [ColumnScope.SoundCloudPlaylistSearch]: 90,
      [ColumnScope.Mixcloud]: 62,
      [ColumnScope.MixcloudShowSearch]: 62,
      [ColumnScope.MixcloudUserSearch]: 90,
    },
    sortOrder: 3,
    minChars: 12,
  },
  [TrackProperty.artist]: {
    name: 'Artist',
    trackProperty: TrackProperty.artist,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 25,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 25,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 30,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 30,
      [ColumnScope.SoundCloud]: 28,
      [ColumnScope.SoundCloudTrackSearch]: 28,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 28,
      [ColumnScope.MixcloudShowSearch]: 28,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 4,
    minChars: 8,
  },
  [TrackProperty.uploader]: {
    name: 'Uploader',
    trackProperty: TrackProperty.uploader,
    // Hidden wherever a source knows who posted a track, absent everywhere
    // else - see the sentinel note on defaultWidthPerc above.
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: -1,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: -1,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: -1,
      [ColumnScope.SoundCloudTrackSearch]: -1,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: -1,
      [ColumnScope.MixcloudShowSearch]: -1,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 13,
    minChars: 8,
  },
  [TrackProperty.album]: {
    name: 'Album',
    trackProperty: TrackProperty.album,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 25,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 25,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 35,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 30,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 5,
    minChars: 8,
  },
  [TrackProperty.genre]: {
    name: 'Genre',
    trackProperty: TrackProperty.genre,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 10,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 10,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 10,
      [ColumnScope.SoundCloudTrackSearch]: 10,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 10,
      [ColumnScope.MixcloudShowSearch]: 10,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 7,
    minChars: 5,
  },
  [TrackProperty.type]: {
    name: 'Type',
    trackProperty: TrackProperty.type,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 12,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 12,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 8,
    calcFromCharCount: true,
  },
  [TrackProperty.releaseDate]: {
    name: 'Year',
    trackProperty: TrackProperty.releaseDate,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 10,
      [ColumnScope.YouTube]: 10,
      [ColumnScope.Spotify]: 10,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 10,
      [ColumnScope.SoundCloud]: 10,
      [ColumnScope.SoundCloudTrackSearch]: 10,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 10,
      [ColumnScope.MixcloudShowSearch]: 10,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 9,
    alignRight: true,
    calcFromCharCount: true,
  },
  [TrackProperty.dateAdded]: {
    name: 'Added',
    trackProperty: TrackProperty.dateAdded,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 10,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 10,
    alignRight: true,
    calcFromCharCount: true,
  },
  /** Cloud providers - file size */
  /**
   * The remaining tag fields. Hidden in the library until ticked on in the
   * Columns menu, absent from the streaming scopes - see the sentinel note
   * on defaultWidthPerc. Hidden columns are sized from their content when
   * unhidden, floored at minChars, so none of them carries a width here.
   */
  [TrackProperty.disc]: {
    name: 'CD',
    trackProperty: TrackProperty.disc,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 1,
    alignRight: true,
    minChars: 3,
  },
  [TrackProperty.albumArtist]: {
    name: 'Album Artist',
    trackProperty: TrackProperty.albumArtist,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 6,
    minChars: 8,
  },
  [TrackProperty.composer]: {
    name: 'Composer',
    trackProperty: TrackProperty.composer,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 14,
    minChars: 8,
  },
  [TrackProperty.comment]: {
    name: 'Comment',
    trackProperty: TrackProperty.comment,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 15,
    minChars: 8,
  },
  [TrackProperty.bpm]: {
    name: 'BPM',
    trackProperty: TrackProperty.bpm,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 16,
    alignRight: true,
    minChars: 4,
  },
  [TrackProperty.initialKey]: {
    name: 'Key',
    trackProperty: TrackProperty.initialKey,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 17,
    minChars: 4,
  },
  [TrackProperty.publisher]: {
    name: 'Publisher',
    trackProperty: TrackProperty.publisher,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 18,
    minChars: 8,
  },
  /**
   * The file fields. Hidden in the library like the tag fields above, and
   * absent from the streaming scopes, where there is no file.
   */
  [TrackProperty.format]: {
    name: 'Format',
    trackProperty: TrackProperty.format,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 19,
    minChars: 4,
  },
  [TrackProperty.bitrate]: {
    name: 'Bit Rate',
    trackProperty: TrackProperty.bitrate,
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: -1,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 20,
    alignRight: true,
    minChars: 8,
  },
  size: {
    name: 'Size',
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 0,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 20,
      [ColumnScope.YouTubeSearch]: 0,
      [ColumnScope.SpotifyAlbumSearch]: 0,
      [ColumnScope.SpotifyPlaylistSearch]: 0,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 0,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 0,
    },
    sortOrder: 11,
    minChars: 6,
    alignRight: true,
  },
  image: {
    name: 'Image',
    defaultWidthPerc: {
      [ColumnScope.LibraryPlaylists]: 0,
      [ColumnScope.YouTube]: 0,
      [ColumnScope.Spotify]: 0,
      [ColumnScope.CloudProviders]: 0,
      [ColumnScope.YouTubeSearch]: 10,
      [ColumnScope.SpotifyAlbumSearch]: 10,
      [ColumnScope.SpotifyPlaylistSearch]: 10,
      [ColumnScope.SpotifyTrackSearch]: 0,
      [ColumnScope.SoundCloud]: 0,
      [ColumnScope.SoundCloudTrackSearch]: 0,
      [ColumnScope.SoundCloudPlaylistSearch]: 10,
      [ColumnScope.Mixcloud]: 0,
      [ColumnScope.MixcloudShowSearch]: 0,
      [ColumnScope.MixcloudUserSearch]: 10,
    },
    sortOrder: 12,
    minChars: 4,
  },
};

/**
 * Columns displayed by default in smaller screen sizes
 */
export const SmallScreenColumns = {
  [ScreenSize.Small]: [TrackProperty.title],
  [ScreenSize.Medium]: [
    TrackProperty.length,
    TrackProperty.title,
    TrackProperty.artist,
    TrackProperty.album,
  ],
};

export interface ColumnSetting {
  width: number;
  sortOrder: number;
  default?: boolean;
}

/**
 * Column settings values for a given scope and screen size.
 *
 * Width in numeric pixels per TrackProperty
 */
export type ColumnSettings = {
  [P in Partial<TrackProperty>]: ColumnSetting;
} & {
  size?: ColumnSetting;
  image?: ColumnSetting;
};
/**
 * The screen sizes a column layout is saved under - every one but
 * X-Large.
 *
 * Small and Medium each get their own layout because they show a
 * different *set* of columns (SmallScreenColumns). Large and X-Large do
 * not differ in any way this file expresses: the default widths are keyed
 * by scope alone, and initializeScope builds both from the same branch.
 * The split only ever existed to make widths answer to the window at all
 * - crossing 1440px handed you a layout built for that width - and
 * fitColumns does that continuously now, so the two are one layout. What
 * the split cost was a column dragged at 1300px vanishing when the window
 * was maximized, because that was the other bucket.
 *
 * X-Large remains a ScreenSize: the playlist controls lay their header
 * row out differently above 1440px, which is nothing to do with columns.
 */
export type ColumnScreenSize = Exclude<ScreenSize, ScreenSize.XLarge>;

/** The layout a screen size reads. Every consumer of the store goes through this. */
export function columnScreenSize(screenSize: ScreenSize): ColumnScreenSize {
  return screenSize === ScreenSize.XLarge ? ScreenSize.Large : screenSize;
}

/**
 * Object with settings for all screen sizes in a given scope.
 */
export type ColumnSettingsScope = {
  [P in ColumnScreenSize]: Partial<ColumnSettings>;
};

export class ColumnSettingsStore {
  [ColumnScope.LibraryPlaylists]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.YouTube]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.YouTubeSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.Spotify]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.SpotifyAlbumSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.SpotifyPlaylistSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.SpotifyTrackSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.SoundCloud]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.SoundCloudTrackSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.SoundCloudPlaylistSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.Mixcloud]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.MixcloudShowSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.MixcloudUserSearch]: Partial<ColumnSettingsScope> = {};
  [ColumnScope.CloudProviders]: Partial<ColumnSettingsScope> = {};

  /**
   * The viewport width each layout was saved at, per scope and screen
   * size. A layout is pixels; shown in a viewport of another width it is
   * fitted from this - see fitColumns. Missing for a layout saved before
   * there was one, and adopted from the first viewport that shows it.
   */
  referenceWidths: ReferenceWidths = {};

  constructor(storeJSON?: ColumnSettingsStore) {
    if (storeJSON) {
      for (const scope in ColumnScope) {
        const columnScope = scope as ColumnScope;

        this[columnScope] = storeJSON[columnScope] || this[columnScope];
      }

      this.referenceWidths = storeJSON.referenceWidths || {};
    }
  }
}

export type ReferenceWidths = Partial<{
  [S in ColumnScope]: Partial<{ [Z in ColumnScreenSize]: number }>;
}>;

export default PlaylistColumns;
